import React, { useState } from "react";
import styled from "styled-components";
import { message } from "antd";
import { creatorListAPI } from "@/api/creatorList";
import { DynamicModal } from "@/shared/components/Modal/DynamicModal";
import { StyledSelect } from "@/shared/components/Select/styledSelect";
import {
	CREATOR_INTEREST_STATUS_OPTIONS,
	getCreatorInterestStatusLabel,
	normalizeCreatorInterestStatus,
} from "../../utils/interestStatusHelper";
import type { CreatorInterestStatus } from "@/shared/types/campaign";

const InterestSelectWrapper = styled.div<{ $inline?: boolean }>`
	display: flex;
	flex-direction: ${(props) => (props.$inline ? "row" : "column")};
	align-items: center;
	gap: ${(props) => (props.$inline ? "8px" : "4px")};
`;

const InterestLabel = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	color: #454545;
	white-space: nowrap;
`;

const InterestStyledSelect = styled(StyledSelect)<{
	$tone?: "yes" | "no" | "none";
	$borderless?: boolean;
}>`
	&& .ant-select-selection-item {
		font-weight: 500;
		color: ${(props) =>
			props.$tone === "yes" ? "#66B026E0" : props.$tone === "no" ? "#C2025B" : "#333"} !important;
	}

	${(props) =>
		props.$borderless &&
		`
		&& .ant-select-selector {
			border: none !important;
			box-shadow: none !important;
			background: transparent !important;
			padding-left: 0 !important;
			padding-right: 20px !important;
		}

		&&:hover .ant-select-selector,
		&&.ant-select-focused .ant-select-selector,
		&&.ant-select-open .ant-select-selector {
			border: none !important;
			box-shadow: none !important;
		}

		&& .ant-select-arrow {
			color: #333 !important;
			right: 0 !important;
		}
	`}
`;

interface CreatorInterestSelectProps {
	creatorId: string;
	assignmentId?: string;
	campaignId?: string;
	blockId?: string;
	interested: CreatorInterestStatus | boolean | undefined;
	onInterestChange?: (creatorId: string, interested: CreatorInterestStatus | undefined) => void;
	showLabel?: boolean;
	yesNoLabels?: boolean;
	labelText?: string;
	placeholderText?: string;
	borderless?: boolean;
	inlineLabel?: boolean;
}

export const CreatorInterestSelect: React.FC<CreatorInterestSelectProps> = ({
	creatorId,
	assignmentId,
	campaignId,
	blockId,
	interested,
	onInterestChange,
	showLabel = false,
	yesNoLabels = false,
	labelText,
	placeholderText,
	borderless = false,
	inlineLabel = false,
}) => {
	const [interestModal, setInterestModal] = useState<{ interested: CreatorInterestStatus } | null>(null);
	const [pendingInterestChange, setPendingInterestChange] = useState<{ value: CreatorInterestStatus } | null>(null);
	const [interestLoading, setInterestLoading] = useState(false);

	const normalizedInterested = normalizeCreatorInterestStatus(interested);
	const currentValue = pendingInterestChange ? pendingInterestChange.value : normalizedInterested || "";

	const tone: "yes" | "no" | "none" =
		currentValue === "interested" ? "yes" : currentValue === "not-interested" ? "no" : "none";

	const placeholder = placeholderText ?? (yesNoLabels ? "Select" : "Reachout Status");
	const resolvedLabel = labelText ?? (yesNoLabels ? "Interested" : "Reachout Status");

	return (
		<InterestSelectWrapper $inline={inlineLabel} data-interest-select>
			{showLabel && <InterestLabel>{resolvedLabel}</InterestLabel>}
			<InterestStyledSelect
				$tone={tone}
				$borderless={borderless}
				value={currentValue}
				placeholder={placeholder}
				size="small"
				style={{
					width: borderless ? 110 : inlineLabel ? 120 : 150,
					maxWidth: borderless ? 110 : inlineLabel ? 120 : 150,
				}}
				options={CREATOR_INTEREST_STATUS_OPTIONS}
				allowClear
				onChange={(e) => {
					const value = normalizeCreatorInterestStatus(e as CreatorInterestStatus | string | undefined);
					if (!value) return;

					setPendingInterestChange({ value });
					setInterestModal({ interested: value });
				}}
			/>

			<DynamicModal
				icon={{ src: "/icon/info.svg" }}
				open={!!interestModal}
				onCancel={() => {
					setPendingInterestChange(null);
					setInterestModal(null);
				}}
				title={labelText ? `Confirm ${labelText} Change` : "Confirm Interest Change"}
				description={
					interestModal
						? `Are you sure you want to mark this creator as ${getCreatorInterestStatusLabel(interestModal.interested)}?`
						: undefined
				}
				confirmText="Yes"
				cancelText="No"
				loading={interestLoading}
				onConfirm={async () => {
					if (!interestModal) return;
					if (!campaignId || !blockId || !assignmentId) {
						message.error("Missing campaign, block, or assignment information");
						setPendingInterestChange(null);
						setInterestModal(null);
						return;
					}

					setInterestLoading(true);
					try {
						await creatorListAPI.updateCreatorInterested(
							campaignId,
							blockId,
							assignmentId,
							interestModal.interested,
						);
						message.success("Creator interest updated successfully!");
						setPendingInterestChange(null);
						setInterestModal(null);
						onInterestChange?.(creatorId, interestModal.interested);
					} catch (error: unknown) {
						console.error("Error updating creator interest:", error);
						const typedError = error as {
							response?: { data?: { error?: string } };
							message?: string;
						};
						const errorMessage =
							typedError.response?.data?.error ||
							typedError.message ||
							"Failed to update creator interest";
						message.error(errorMessage);
						setPendingInterestChange(null);
						setInterestModal(null);
					} finally {
						setInterestLoading(false);
					}
				}}
			/>
		</InterestSelectWrapper>
	);
};
